import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import { sessionStore, useSession } from '@/core/auth/SessionStore'
import { extractExpiresAtMs } from '@/core/auth/jwt'
import { db, deleteDatabase } from '@/core/db/dexie'

/** Lançamentos e fotos que ainda não chegaram ao servidor (inclui recusados, que o operador pode reabrir). */
async function contarNaoEnviados(): Promise<number> {
  try {
    const [prod, foto, carr, pd] = await Promise.all([
      db.out_producao.where('syncStatus').notEqual('SYNCED').count(),
      db.out_foto.where('syncStatus').notEqual('SYNCED').count(),
      db.out_carreteiro.where('syncStatus').notEqual('SYNCED').count(),
      db.out_parte_diaria.where('syncStatus').notEqual('SYNCED').count(),
    ])
    return prod + foto + carr + pd
  } catch {
    return 0
  }
}

async function limparDadosLocais() {
  // Limpa caches do SW + DB inteiro — evita vazar dados do user A para o user B.
  if ('caches' in window) {
    const keys = await caches.keys()
    await Promise.all(keys.filter((k) => !k.startsWith('workbox-precache')).map((k) => caches.delete(k)))
  }
  try {
    await deleteDatabase()
  } catch (e) {
    console.warn('[auth] falha ao limpar Dexie:', e)
  }
}

interface UseAuthResult {
  session: ReturnType<typeof useSession>
  login: (usuario: string, senha: string) => Promise<void>
  logout: () => Promise<void>
  loading: boolean
  error: string | null
}

/**
 * Hook único para login/logout. Lida com:
 *  - Persistência via SessionStore
 *  - Navegação pós-login (home) e pós-logout (login)
 *  - Limpeza completa (Dexie + caches do SW) no logout — adicionado em Fase 2/6
 */
export function useAuth(): UseAuthResult {
  const navigate = useNavigate()
  const session = useSession()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = useCallback(
    async (usuario: string, senha: string) => {
      setLoading(true)
      setError(null)
      try {
        const resp = await authEndpoints.login({ usuario, senha })
        // Entrar de novo (token vencido) com o MESMO usuário preserva projeto,
        // obra, catálogos, rascunhos e outbox. Outro usuário neste aparelho só
        // entra se não houver nada pendente do anterior — senão os lançamentos
        // dele sairiam com o token (e o nome) de outra pessoa.
        const anterior = sessionStore.current()
        const mesmoUsuario = anterior != null && anterior.userId === resp.user.id
        if (anterior && !mesmoUsuario) {
          const pendentes = await contarNaoEnviados()
          if (pendentes > 0) {
            setError(
              `Este aparelho tem ${pendentes} lançamento(s) de ${anterior.userNome} ainda não enviados. ` +
                'Entre com esse usuário para enviar, ou toque em "Continuar sem internet" e use Sair para descartá-los.',
            )
            return
          }
          await limparDadosLocais()
        }
        const expiresAtEpochMs = resp.expiresAt
          ? new Date(resp.expiresAt).getTime()
          : extractExpiresAtMs(resp.token)
        sessionStore.save({
          token: resp.token,
          expiresAtEpochMs,
          userId: resp.user.id,
          userNome: resp.user.nome,
          userChapa: resp.user.chapa ?? null,
          projetos: resp.user.projetos,
          projetoAtualId: mesmoUsuario ? anterior.projetoAtualId : null,
          obraAtualId: mesmoUsuario ? anterior.obraAtualId : null,
        })
        // Pede persistência defensiva — iOS Safari pode recusar mas tentamos.
        if (navigator.storage?.persist) {
          navigator.storage.persist().catch(() => {})
        }
        navigate('/', { replace: true })
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setError('Usuário ou senha inválidos')
        } else if (e instanceof ApiError && e.isOffline) {
          setError('Sem conexão. Verifique sua rede.')
        } else {
          setError(e instanceof Error ? e.message : 'Erro inesperado')
        }
      } finally {
        setLoading(false)
      }
    },
    [navigate],
  )

  const logout = useCallback(async () => {
    // Sair apaga o banco local. Com coisa não enviada, isso é perda de dados —
    // e sem internet não há como entrar de novo. Exige confirmação explícita.
    const pendentes = await contarNaoEnviados()
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    if (pendentes > 0) {
      const ok = window.confirm(
        `Há ${pendentes} lançamento(s)/foto(s) que ainda NÃO foram enviados ao servidor.\n\n` +
          'Sair agora APAGA esses dados deste aparelho.\n\nDeseja sair mesmo assim?',
      )
      if (!ok) return
    } else if (offline) {
      const ok = window.confirm(
        'Você está sem internet. Se sair agora, só conseguirá entrar de novo quando tiver conexão.\n\nDeseja sair mesmo assim?',
      )
      if (!ok) return
    }
    sessionStore.clear()
    await limparDadosLocais()
    navigate('/login', { replace: true })
  }, [navigate])

  return { session, login, logout, loading, error }
}
